import { Stack, Typography } from '@mui/material';

import ScanningDocument from './ScanningDocument';
import narrateProgress from './narrateProgress';
import { LOADING_LOOP_MS } from './timeline';
import useProcessingStage from './useProcessingStage';

import TwinklingSparkle from 'modules/common/components/TwinklingSparkle';

/**
 * Shown once the wait has run long enough to be worth excusing the member from.
 *
 * The work carries on without anyone watching it, so the only thing this changes is whether the
 * member knows that.
 */
const CLOSE_HINT = 'You can close this. We’ll keep working and have them ready shortly.';

interface StatementProcessingContentProps {
    /**
     * How many statements this batch holds, for the copy that quotes it.
     *
     * Zero where nothing knows yet — the gap between the transfer landing and the server naming
     * the documents — and the copy drops the number rather than inventing one.
     */
    readonly statementCount: number;
    /**
     * When the batch was uploaded, as epoch milliseconds — the anchor the windows are timed
     * from. Omitted during the upload itself, where no document exists to have a timestamp yet
     * and mount is the only start there is.
     */
    readonly startedAtMs?: number;
}

/**
 * What the statement dropzone shows while it is working, in place of the idle prompt.
 *
 * Passed to `DragAndDrop` as `loadingContent` for the same reason the idle copy goes through
 * `idleContent`: the shape of this message — a scanning illustration, a narrated line, and a
 * note that the member can leave — is this surface's decision, and the shared uploader needs to
 * know none of it.
 *
 * The narration is a fixed sequence on the clock: uploading, then scanning, then processing, and
 * after ten seconds the note inviting the member to close the modal. Nothing here reports on the
 * server — how far through the batch it is decides only how long this screen stays up, which is
 * the caller's to say.
 */
export default function StatementProcessingContent({
    statementCount,
    startedAtMs = undefined,
}: Readonly<StatementProcessingContentProps>) {
    const { stage, showCloseHint } = useProcessingStage(startedAtMs);
    const label = narrateProgress(stage, statementCount);

    return (
        <Stack direction="column" alignItems="center" spacing={2}>
            <ScanningDocument />

            {/* No gap: the design sets the note tight under the line it qualifies. */}
            <Stack direction="column" alignItems="center">
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                    {/* On the screen's own loop, so it reads as one motion with the scan sweep. */}
                    <TwinklingSparkle loopMs={LOADING_LOOP_MS} />
                    {/*
                     * Hidden from assistive tech, and the one line here that is. The overlay
                     * around this is a polite live region, so a line that changes three times in
                     * ten seconds would be announced each time over a member who has already
                     * been told what is happening. The region's own label says that once, and
                     * the note below — which appears once, and tells the member something they
                     * can act on — is left audible.
                     */}
                    <Typography variant="body2" textAlign="center" aria-hidden>
                        {label}
                    </Typography>
                </Stack>

                {showCloseHint && (
                    <Typography variant="caption" color="text.secondary" textAlign="center">
                        {CLOSE_HINT}
                    </Typography>
                )}
            </Stack>
        </Stack>
    );
}
